<template>
  <v-sheet
    elevation="15"
    class="footer d-flex justify-space-between align-center pa-4 w-100 position-sticky"
  >
    <v-btn
      v-if="showBack"
      class="ma-2 text-none"
      color="grey-darken-1"
      prepend-icon="mdi-arrow-left"
      @click="handleBack"
      :disabled="disableBack"
    >
      {{ $t('labels.back') }}
    </v-btn>

    <v-btn
      v-if="showReset"
      class="ma-2 text-none"
      color="grey-darken-1"
      prepend-icon="mdi-refresh"
      @click="$emit('reset')"
      :disabled="disableReset"
      :loading="loadingReset"
    >
      {{ $t('labels.reset') }}
    </v-btn>

    <v-btn
      v-if="showRestart"
      class="ma-2 text-none"
      color="grey-darken-1"
      prepend-icon="mdi-restart"
      @click="handleRestart"
    >
      {{ $t('labels.restart') }}
    </v-btn>

    <v-btn
      v-if="showRetry"
      :disabled="disableRetry"
      class="ma-2 text-none"
      color="grey-darken-1"
      prepend-icon="mdi-restart"
      @click="$emit('retry')"
    >
      {{ $t('labels.retry') }}
    </v-btn>

    <v-spacer></v-spacer>

    <div class="d-flex">
      <v-btn
        v-if="showAutoFix"
        color="secondary"
        class="ma-2"
        @click="$emit('autoFix')"
        :loading="loadingAutoFix"
        :disabled="disableAutoFix"
      >
        {{ $t('labels.autofix') }}
      </v-btn>
      <v-btn
        v-if="false"
        color="secondary"
        class="ma-2"
        :loading="loadingEdit"
        @click="$emit('edit')"
      >
        {{ $t('labels.edit') }}
      </v-btn>

      <v-btn
        v-if="showGenerate"
        color="primary"
        class="ma-2"
        append-icon="mdi-arrow-right"
        :disabled="disableGenerate"
        :loading="loadingGenerate"
        @click="$emit('generate')"
      >
        <template #prepend>
          <v-badge v-if="canvasStore.counter" inline :content="canvasStore.counter" color="primary">
          </v-badge>
        </template>
        {{
          appStore.selectedDesignFlow.value === designFlows.upload.value
            ? $t('labels.validate')
            : appStore.selectedDesignFlow.value === designFlows.convert.value
              ? $t('labels.convert')
              : $t('labels.generate')
        }}
      </v-btn>

      <v-btn
        v-if="showSelect"
        color="primary"
        class="ma-2"
        append-icon="mdi-arrow-right"
        :disabled="disableSelect"
        :loading="loadingSelect"
        @click="$emit('select')"
      >
        {{ $t('labels.select') }}
      </v-btn>

      <v-btn
        color="primary"
        class="ma-2"
        append-icon="mdi-arrow-right"
        :loading="loadingNext"
        :disabled="disableNext"
        v-if="showNext"
        @click="$emit('next')"
      >
        {{ $t('labels.next') }}
      </v-btn>

      <v-btn-group v-if="showFeedback" class="mr-6">
        <v-tooltip location="bottom" open-delay="300">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-thumb-up"
              variant="plain"
              :color="userFeedback?.reaction === reactions.LIKE ? 'primary' : 'grey'"
              size="medium"
              class="pa-2"
              @click="openFeedbackMenu(reactions.LIKE)"
            ></v-btn>
          </template>
          <span>{{ $t('labels.like') }}</span>
        </v-tooltip>
        <v-tooltip location="bottom" open-delay="300">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-thumb-down"
              variant="plain"
              :color="userFeedback?.reaction === reactions.DISLIKE ? 'primary' : 'grey'"
              size="medium"
              class="pa-2"
              @click="openFeedbackMenu(reactions.DISLIKE)"
            ></v-btn>
          </template>
          <span>{{ $t('labels.dislike') }}</span>
        </v-tooltip>
        <v-tooltip location="bottom" open-delay="300">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-heart"
              variant="plain"
              :color="userFeedback?.reaction === reactions.LOVE ? 'primary' : 'grey'"
              size="medium"
              class="pa-2"
              @click="openFeedbackMenu(reactions.LOVE)"
            ></v-btn>
          </template>
          <span>{{ $t('labels.love') }}</span>
        </v-tooltip>
      </v-btn-group>

      <v-menu v-if="showDownload">
        <template v-slot:activator="{ props }">
          <v-btn color="primary" v-bind="props" variant="outlined" size="large" class="px-4">
            <v-icon start>mdi-download</v-icon>
            {{ $t('labels.download') }}
          </v-btn>
        </template>
        <v-list density="comfortable" class="py-0">
          <v-list-item
            v-for="(item, index) in ['Download as PNG', 'Copy URL']"
            :key="index"
            :value="item"
            @click="downloadOrCopyUrl(item)"
          >
            <template v-slot:prepend>
              <v-icon
                :icon="item === 'Download as PNG' ? 'mdi-download' : 'mdi-content-copy'"
                size="small"
                class="mr-n5"
              ></v-icon>
            </template>
            <v-list-item-title>{{ item }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </v-sheet>
  <AppDialog
    v-model="feedbackDialog"
    :title="userFeedback ? $t('labels.updateFeedback') : $t('labels.feedback')"
    :cancel-text="$t('labels.cancel')"
    :confirm-text="userFeedback ? $t('labels.update') : $t('labels.submit')"
    :confirming="submittingFeedback"
    @confirm="submitFeedback"
    @cancel="clearFeedback"
  >
    <template #content>
      <v-container>
        <v-row class="mb-2">
          <v-col>
            <v-btn
              block
              class="text-capitalize"
              :ripple="false"
              :color="userReaction == reactions.DISLIKE ? 'primary' : ''"
              :variant="userReaction == reactions.DISLIKE ? 'flat' : 'tonal'"
              :prepend-icon="
                userReaction == reactions.DISLIKE ? 'mdi-thumb-down' : 'mdi-thumb-down-outline'
              "
              @click="userReaction = reactions.DISLIKE"
            >
              {{ $t('labels.dislike') }}
            </v-btn>
          </v-col>
          <v-col>
            <v-btn
              block
              class="text-capitalize"
              :ripple="false"
              :color="userReaction == reactions.LIKE ? 'primary' : ''"
              :variant="userReaction == reactions.LIKE ? 'flat' : 'tonal'"
              :prepend-icon="
                userReaction == reactions.LIKE ? 'mdi-thumb-up' : 'mdi-thumb-up-outline'
              "
              @click="userReaction = reactions.LIKE"
            >
              {{ $t('labels.like') }}
            </v-btn>
          </v-col>
          <v-col>
            <v-btn
              block
              class="text-capitalize"
              :ripple="false"
              :color="userReaction == reactions.LOVE ? 'primary' : ''"
              :variant="userReaction == reactions.LOVE ? 'flat' : 'tonal'"
              :prepend-icon="userReaction == reactions.LOVE ? 'mdi-heart' : 'mdi-heart-outline'"
              @click="userReaction = reactions.LOVE"
            >
              {{ $t('labels.love') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-textarea
          v-model="comment"
          variant="outlined"
          rows="3"
          :label="$t('labels.commentOptional')"
          color="primary"
        ></v-textarea>
      </v-container>
    </template>
  </AppDialog>
</template>

<script lang="ts" setup>
import { inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import AppDialog from './AppDialog.vue'

import AppService from '@/services/appService'
import { useAppStore } from '@/stores/app'
import { useCanvasStore } from '@/stores/canvas'
import { useUndoRedoStore } from '@/stores/undoRedo'
import { useEditorStore } from '@/stores/editor'
import { useAssetStore } from '@/stores/asset'
import { downloadFilefromUrl } from '@/helpers/commonHelper'
import { useRouter } from 'vue-router'
import { useAutofixStore } from '@/stores/autofix'
import designFlows from '@/constants/designFlows'

defineEmits(['next', 'reset', 'autoFix', 'edit', 'generate', 'select', 'retry'])

withDefaults(
  defineProps<{
    loadingNext?: boolean
    loadingReset?: boolean
    loadingEdit?: boolean
    loadingAutoFix?: boolean
    loadingGenerate?: boolean
    loadingSelect?: boolean
    disableReset?: boolean
    disableNext?: boolean
    disableAutoFix?: boolean
    disableGenerate?: boolean
    disableSelect?: boolean
    disableRetry?: boolean
    disableBack?: boolean
    showAutoFix?: boolean
    showReset?: boolean
    showBack?: boolean
    showNext?: boolean
    showEdit?: boolean
    showFeedback?: boolean
    showDownload?: boolean
    showRestart?: boolean
    showGenerate?: boolean
    showSelect?: boolean
    showRetry?: boolean
  }>(),
  {
    loadingNext: false,
    loadingReset: false,
    loadingEdit: false,
    loadingAutoFix: false,
    loadingGenerate: false,
    loadingSelect: false,
    disableReset: false,
    disableNext: false,
    disableAutoFix: false,
    disableGenerate: false,
    disableSelect: false,
    disableRetry: false,
    showAutoFix: false,
    showReset: false,
    showBack: false,
    showNext: false,
    showEdit: false,
    showFeedback: false,
    showDownload: false,
    showRestart: false,
    showGenerate: false,
    showSelect: false,
    showRetry: false,
  },
)

interface Feedback {
  id: number | null
  reaction: number
  comment: string | null
}

const reactions = { LIKE: 1, DISLIKE: 2, LOVE: 3 }

const canvasStore = useCanvasStore()
const appStore = useAppStore()
const assetStore = useAssetStore()
const undoRedoStore = useUndoRedoStore()
const editorStore = useEditorStore()
const autofixStore = useAutofixStore()
const appService = new AppService()
const { t } = useI18n()
const notify = inject('notify') as Function
const router = useRouter()

const feedbackDialog = ref<boolean>(false)
const userReaction = ref<number | null>(null)
const comment = ref<string | null>(null)
const submittingFeedback = ref<boolean>(false)
const userFeedback = ref<Feedback | null>(null)

const handleBack = () => {
  window.history.back()
}

const handleRestart = () => {
  editorStore.isAutoFix = false
  autofixStore.resetAutofixState()
  autofixStore.isAutofixHappening = false
  undoRedoStore.clearHistory()
  editorStore.resetEditorStore()
  router.push({ name: 'Mode' })
}

const openFeedbackMenu = (reaction: number) => {
  userReaction.value = reaction
  feedbackDialog.value = true
}

const clearFeedback = () => {
  if (userFeedback.value) {
    userReaction.value = userFeedback.value.reaction
    comment.value = userFeedback.value.comment
  } else {
    userReaction.value = null
    comment.value = null
  }
  feedbackDialog.value = false
}

const submitFeedback = async () => {
  try {
    submittingFeedback.value = true
    if (userReaction.value) {
      if (userFeedback.value && userFeedback.value.id) {
        await appService.updateCreativeFeedback(
          appStore.selectedBusinessProfileId,
          userFeedback.value.id,
          userReaction.value,
          comment.value,
        )
        notify(t('messages.feedbackUpdatedSuccesfully'), 'green')
        userFeedback.value.reaction = userReaction.value
        userFeedback.value.comment = comment.value
      } else {
        const feedbackId = await appService.submitCretiveFeedback(
          appStore.selectedBusinessProfileId,
          userReaction.value,
          comment.value,
          assetStore.creativeS3Url,
        )
        notify(t('messages.feedbackSubmittedSuccesfully'), 'green')
        userFeedback.value = {
          id: feedbackId,
          reaction: userReaction.value,
          comment: comment.value,
        }
      }
      feedbackDialog.value = false
    }
  } catch {
    notify(t('errors.somethingWentWrong'), 'error')
  } finally {
    submittingFeedback.value = false
  }
}

const downloadOrCopyUrl = (item: string) => {
  if (item === 'Download as PNG') {
    downloadCreative()
  } else {
    navigator.clipboard.writeText(assetStore.creativeS3Url)
    notify('URL copied to clipboard', 'green')
  }
}

const downloadCreative = async () => {
  if (!assetStore.creativeS3Url) return

  try {
    downloadFilefromUrl(assetStore.creativeS3Url, 'creative.png')
  } catch (error) {
    console.error(t('errors.somethingWentWrong'), error)
  }
}
</script>

<style scoped>
.footer {
  bottom: 0;
  height: 10vh;
  z-index: 100;
}

:deep(.v-badge__badge) {
  font-weight: bold;
}
</style>
